import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { COMMAND_FIELDS } from './model';

/**
 * E37 · the practice's check-in commands from the portal — RecordCheckInInSession and DeleteCheckIn (the hub's
 * Remove) — each ONE deliberate act (a button), posted with `errorNotification: false` so the screen reports the
 * outcome itself (portal conventions): a CODED refusal (`checkIn.errors.*`, already in the therapist's language) is
 * shown verbatim, against the thing it refused; anything else is the screen's own copy plus Sentry.
 */
export type CheckInOperation = 'recordCheckInInSession' | 'deleteCheckIn';

const INPUT_TYPE: Record<CheckInOperation, string> = {
  recordCheckInInSession: 'RecordCheckInInSessionInput!',
  deleteCheckIn: 'DeleteCheckInInput!',
};

export type CheckInCommandResult =
  | { ok: true; checkInId: string; recorded: boolean }
  | { ok: false; code: string | undefined; message: string };

export const useCheckInCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{
    checkInId: string;
    recorded: boolean;
  }>();

  const run = useCallback(
    async (
      operation: CheckInOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<CheckInCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: COMMAND_FIELDS,
            variables: {
              input: {
                name: 'input',
                type: INPUT_TYPE[operation],
                value: input,
              },
            },
          },
        });
        const data = res?.data;
        if (!data?.checkInId) {
          // a null answer is a refusal, never a save (portal conventions)
          return { ok: false, code: undefined, message: fallback };
        }
        return {
          ok: true,
          checkInId: data.checkInId,
          recorded: !!data.recorded,
        };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
